// PushSubscribeButton — small toggle that the user can click to enable/disable
// background push notifications (uses Web Push + VAPID via /api/push/subscribe).
//
// Reflects three states:
//   • PUSH ON     (granted + subscription registered)
//   • ENABLE PUSH (granted? subscribe / not-granted? request perm + subscribe)
//   • PUSH OFF    (denied or unsupported — informational only)
//
// Hidden entirely when the system.web_push flag is OFF.

import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import { NotificationService } from '../utils/notifications';
import { usePushSubscribe } from '../hooks/usePushSubscribe';

export function PushSubscribeButton({ className = '', testId = 'push-toggle', iconOnlyOnMobile = false, variant = 'code' }) {
  const { ensureSubscribed, unsubscribe, isSubscribed, enabled } = usePushSubscribe();
  const [permStatus, setPermStatus] = useState(() => NotificationService.getPermissionStatus());

  // Auto-attempt subscribe on mount when the user already granted permission
  useEffect(() => {
    if (enabled && permStatus === 'granted') {
      ensureSubscribed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, permStatus]);

  const handleToggle = useCallback(async () => {
    if (!enabled) return;

    if (permStatus === 'ios-needs-pwa') {
      toast.info('Add to Home Screen for push', {
        description: 'Tap Share → "Add to Home Screen" first. iOS only delivers push to installed PWAs.',
        duration: 8000,
      });
      return;
    }
    if (permStatus === 'unsupported') {
      toast.info('Push not supported in this browser');
      return;
    }
    if (permStatus === 'denied') {
      toast.error('Notifications are blocked', {
        description: 'Re-allow notifications in browser site settings.',
        duration: 8000,
      });
      return;
    }

    if (isSubscribed) {
      const ok = await unsubscribe();
      if (ok) toast.info('Push notifications turned off');
      return;
    }

    if (permStatus !== 'granted') {
      const granted = await NotificationService.requestPermission();
      setPermStatus(NotificationService.getPermissionStatus());
      if (!granted) {
        toast.error('Permission not granted');
        return;
      }
    }
    const ok = await ensureSubscribed();
    toast[ok ? 'success' : 'error'](
      ok ? 'Push notifications enabled.' : 'Could not enable push (try again).'
    );
  }, [enabled, permStatus, isSubscribed, ensureSubscribed, unsubscribe]);

  if (!enabled) return null;

  let label = 'ENABLE PUSH';
  let Icon = Bell;
  let colorCls = 'border-[#2A0F3D] text-[#A3C7A8] hover:border-[#39FF14] hover:text-[#39FF14]';
  if (isSubscribed && permStatus === 'granted') {
    label = 'PUSH ON';
    colorCls = 'border-[#39FF14] text-[#39FF14] hover:bg-[rgba(57,255,20,0.08)]';
  } else if (permStatus === 'denied') {
    label = 'PUSH BLOCKED';
    Icon = BellOff;
    colorCls = 'border-[#FF3B30] text-[#FF3B30]';
  } else if (permStatus === 'ios-needs-pwa') {
    label = 'INSTALL PWA';
    Icon = BellOff;
    colorCls = 'border-[#00F0FF] text-[#00F0FF]';
  } else if (permStatus === 'unsupported') {
    label = 'PUSH N/A';
    Icon = BellOff;
    colorCls = 'border-[#4B6B52] text-[#4B6B52]';
  }

  if (variant === 'modern') {
    const on = isSubscribed && permStatus === 'granted';
    const text = on ? 'Alerts on' : permStatus === 'denied' ? 'Alerts blocked'
      : permStatus === 'ios-needs-pwa' ? 'Install app for alerts' : permStatus === 'unsupported' ? 'Alerts N/A' : 'Turn on alerts';
    return (
      <button type="button" onClick={handleToggle} className={`am-chip ${className}`} data-testid={testId} aria-label={text}
        style={{ border: 0, cursor: 'pointer', background: on ? 'var(--am-emerald-50)' : undefined, color: on ? 'var(--am-emerald-700)' : undefined }}>
        <Icon size={12} /> {text}
      </button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleToggle}
      className={`rounded-none font-mono text-xs ${colorCls} ${className}`}
      data-testid={testId}
      title={label}
      aria-label={label}
    >
      <Icon className={`w-3 h-3 ${iconOnlyOnMobile ? 'sm:mr-1' : 'mr-1'}`} />
      <span className={iconOnlyOnMobile ? 'hidden sm:inline' : ''}>{label}</span>
    </Button>
  );
}
